import React, { JSX, useState } from 'react'
import { SignalIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest } from 'lib/helpers'
import { StreamsReviewerAccessResult } from 'types/api'
import setStreamsReviewerAccessMutation from 'graphql/setStreamsReviewerAccess.graphql'

export const STREAMS_REVIEWER_COPY = {
  title: 'Streams access (reviewer)',
  body: 'Shows Streams to a chosen account while Streams are coming soon for everyone else. For the FlightDeck demo login that Google\'s reviewers use. It gives no staff powers and changes no plan or limit: what the account can do inside a stream still comes from its plan and its stream roles. The person must have signed in to FlightDeck once. By user email; we find the account for you.',
  placeholder: 'user@example.com',
  turnOn: 'Turn on',
  turnOff: 'Turn off',
  fallbackError: 'Failed to set Streams access (reviewer)'
} as const

export const streamsReviewerResultCopy = (email: string, res: StreamsReviewerAccessResult): string => {
  if (res.streamsReviewerAccess) {
    return `${email} Streams access (reviewer) is on.${res.previousValue ? ' (was already on)' : ''}`
  }
  return `${email} Streams access (reviewer) is off.${res.previousValue ? '' : ' (was already off)'}`
}

export const requestStreamsReviewerAccess = async (
  auth0Token: string,
  email: string,
  enabled: boolean
): Promise<StreamsReviewerAccessResult> => {
  const { result } = await graphqlRequest<{ result: StreamsReviewerAccessResult }>(
    setStreamsReviewerAccessMutation, { email, enabled }, auth0Token
  )
  return result
}

export type StreamsReviewerBusy = 'on' | 'off' | null

export const StreamsReviewerAccessCardView = ({
  email,
  busy,
  resultText,
  error,
  onEmailChange,
  onSubmit,
  onDismissError
}: {
  email: string
  busy: StreamsReviewerBusy
  resultText: string | null
  error: string | null
  onEmailChange: (value: string) => void
  onSubmit: (enabled: boolean) => void
  onDismissError: () => void
}): JSX.Element => {
  const disabled = busy != null || email.trim().length === 0
  return (
    <section
      data-testid='streams-reviewer-access-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center gap-2'>
        <SignalIcon className='size-5 text-brand' aria-hidden='true' />
        <h3 className='text-base font-semibold text-dm-text-primary'>{STREAMS_REVIEWER_COPY.title}</h3>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>{STREAMS_REVIEWER_COPY.body}</p>
      <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <input
          type='email'
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder={STREAMS_REVIEWER_COPY.placeholder}
          disabled={busy != null}
          data-testid='streams-reviewer-access-email-input'
          className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
        />
        <div className='flex shrink-0 items-center gap-2'>
          <button
            type='button'
            onClick={() => onSubmit(true)}
            disabled={disabled}
            data-testid='streams-reviewer-access-on-button'
            className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'on' && <LoadingSpinner />}
            {STREAMS_REVIEWER_COPY.turnOn}
          </button>
          <button
            type='button'
            onClick={() => onSubmit(false)}
            disabled={disabled}
            data-testid='streams-reviewer-access-off-button'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'off' && <LoadingSpinner />}
            {STREAMS_REVIEWER_COPY.turnOff}
          </button>
        </div>
      </div>
      {resultText != null && (
        <p data-testid='streams-reviewer-access-result' className='mt-3 text-xs text-emerald-300'>
          {resultText}
        </p>
      )}
      {error != null && (
        <div className='mt-3' data-testid='streams-reviewer-access-error'>
          <ErrorInline onDismiss={onDismissError}>{error}</ErrorInline>
        </div>
      )}
    </section>
  )
}

export const StreamsReviewerAccessCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState<StreamsReviewerBusy>(null)
  const [result, setResult] = useState<{ email: string, res: StreamsReviewerAccessResult } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (enabled: boolean): Promise<void> => {
    const trimmed = email.trim()
    if (trimmed.length === 0 || busy != null) return
    setBusy(enabled ? 'on' : 'off')
    setError(null)
    setResult(null)
    try {
      const res = await requestStreamsReviewerAccess(auth0Token, trimmed, enabled)
      setResult({ email: trimmed, res })
    } catch (err) {
      setError(err instanceof Error ? err.message : STREAMS_REVIEWER_COPY.fallbackError)
    } finally {
      setBusy(null)
    }
  }

  return (
    <StreamsReviewerAccessCardView
      email={email}
      busy={busy}
      resultText={result != null ? streamsReviewerResultCopy(result.email, result.res) : null}
      error={error}
      onEmailChange={setEmail}
      onSubmit={(enabled) => { void submit(enabled) }}
      onDismissError={() => setError(null)}
    />
  )
}
